import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { isDesktop } from 'react-device-detect';

import Avatar from 'components/Avatar';
import dayjs from 'dayjs';
import { SET_SHOW_PAUSED_MODAL } from 'containers/App/constants';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import { useFloatingTooltip } from 'hooks/useFloatingTooltip';

dayjs.extend(customParseFormat);

const DATE_FORMATS = [
  {
    label: 'MMMM D, YYYY',
    value: 'mm/dd/yyyy',
  },
  {
    label: 'D MMMM, YYYY',
    value: 'dd/mm/yyyy',
  },
  {
    label: 'YYYY MMMM D',
    value: 'yyyy/mm/dd',
  },
];

const formatDate = (str, dateFormat) => {
  if (!str || !dateFormat) return '';
  return dayjs(str).format(
    DATE_FORMATS.find((format) => format.value === dateFormat).label,
  );
};

const getWeddingTooltipMessage = (project, isActive) => {
  if (isActive) {
    return '';
  }
  const { archivedAt, archivedByFirstname, archivedByLastname } = project;
  const date = dayjs(archivedAt ?? '').format('MMM DD, YYYY');
  const time = dayjs(archivedAt ?? '').format('hh:mm A');

  return archivedAt
    ? `Archived by ${archivedByFirstname} ${archivedByLastname} on ${date} at ${time}`
    : 'No archived data available';
};

const showPausedModal = (dispatch) => {
  dispatch({ type: SET_SHOW_PAUSED_MODAL, payload: true });
};

const handleProjectCardClick = (
  goToProject,
  dispatch,
  weddingId,
  projectIsPaused,
  isProcessing = false,
  isError = false,
) => {
  if (isProcessing || isError) return;
  if (projectIsPaused) {
    dispatch({ type: SET_SHOW_PAUSED_MODAL, payload: true });
  }
  else {
    goToProject(weddingId);
  }
};

export const ListItem = ({
  project,
  options,
  action1,
  name1,
  action2,
  name2,
  goToProject,
  isActive,
}) => {
  const { datePreference } = useSelector(
    (state) => state.global.userInfo.organization,
  );
  const { isPaused, organization } = useSelector((state) => state.global.userInfo);
  const { id: orgId } = organization;
  const dispatch = useDispatch();
  const dateFormat = datePreference || 'mm/dd/yyyy';
  const { weddingLeadName, weddingAddress, serviceType, weddingId, creationStatus = '' } = project;
  const { projectLead: showLead, location: showLocation, service: showService } = options;
  const weddingShortname = project.image100 === '' ? project.weddingShortname : '';
  const isPausedProject = isPaused && project.orgId === orgId;
  const isProcessing = creationStatus === 'processing';
  const isError = creationStatus === 'failed';
  const weddingTooltipMessage = getWeddingTooltipMessage(project, isActive);
  const {
    showTooltip,
    hideTooltip,
    updateTooltip,
    tooltipElement,
  } = useFloatingTooltip(weddingTooltipMessage);

  return (
    <div
      onClick={() => handleProjectCardClick(goToProject, dispatch, weddingId, isPausedProject, isProcessing, isError)}
      className={clsx(
        'project-item',
        'project-list-item',
        (isDesktop && !isError) && 'hover-expose',
        isProcessing && 'is-processing',
        isError && 'failed-creation',
      )}
    >
      <div className="project-item__avatar project-list-item__avatar">
        <Avatar
          photo={project.image100}
          text={weddingShortname}
          size={80}
          borderSize={5}
          showTooltip={showTooltip}
          hideTooltip={hideTooltip}
          updateTooltip={updateTooltip}
          tooltipElement={tooltipElement}
        />
      </div>
      <div className="project-list-item__content">
        <div className="project-item__name project-list-item__name">
          {project.weddingName}
        </div>
        <div className="project-item__date project-list-item__date">
          {formatDate(project.weddingDate, dateFormat)}
        </div>
        <div className="project-item__details project-list-item__details">
          {showService && serviceType && (
            <span className="project-item__metadata project-item__service-type project-card__service-type">
              {serviceType}
            </span>
          )}
          {showLocation && weddingAddress && (
            <span className="project-item__metadata project-item__location project-card__location">
              {weddingAddress}
            </span>
          )}
          {showLead && weddingLeadName && (
            <span className="project-item__metadata project-item__lead-assignment project-card__lead-assignment">
              {weddingLeadName}
            </span>
          )}
        </div>
      </div>
      {isProcessing && (
        <div className="processing">Processing...</div>
      )}
      {isError && (
        <div className="project-item__actions project-list-item__actions">
          <span className="error-message">Error creating project</span>
          <button data-id={project.weddingId}
            className='button--unstyled'
            onClick={isPausedProject ? () => showPausedModal(dispatch) : action2}
          >
            {name2}
          </button>
        </div>
      )}
      {(project.isOwner && !isError && !isProcessing) && (
        <div className="project-item__actions project-list-item__actions">
          <button data-id={project.weddingId}
            className="button--unstyled"
            onClick={isPausedProject ? () => showPausedModal(dispatch) : action1}
          >
            {name1}
          </button>
          <button data-id={project.weddingId}
            className="button--unstyled"
            onClick={isPausedProject ? () => showPausedModal(dispatch) : action2}
          >
            {name2}
          </button>
        </div>
      )}
    </div>
  );
};

export const GridItem = ({
  project,
  options,
  action1,
  name1,
  action2,
  name2,
  goToProject,
  isActive,
}) => {
  const { datePreference } = useSelector(
    (state) => state.global.userInfo.organization || {},
  );
  const { isPaused, organization } = useSelector((state) => state.global.userInfo);
  const { id: orgId } = organization || {};
  const dispatch = useDispatch();
  const dateFormat = datePreference || 'mm/dd/yyyy';
  const { weddingLeadName, weddingAddress, serviceType, weddingId, creationStatus = '' } = project;
  const { projectLead: showLead, location: showLocation, service: showService } = options;
  const isPausedProject = isPaused && project.orgId === orgId;
  const isProcessing = creationStatus === 'processing';
  const isError = creationStatus === 'failed';

  const weddingShortname = project.image100 === '' ? project.weddingShortname : '';

  const weddingTooltipMessage = getWeddingTooltipMessage(project, isActive);
  const {
    showTooltip,
    hideTooltip,
    updateTooltip,
    tooltipElement,
  } = useFloatingTooltip(weddingTooltipMessage);

  return (
    <div
      onClick={() => handleProjectCardClick(goToProject, dispatch, weddingId, isPausedProject)}
      className={clsx(
        'project-item',
        'project-card',
        (isDesktop && !isError) && 'hover-expose',
        isProcessing && 'is-processing',
        isError && 'failed-creation',
      )}
    >
      <div className="project-item__avatar project-card__avatar">
        <Avatar
          photo={project.image306}
          text={weddingShortname}
          size={235}
          borderSize={6}
          className={clsx(
            'avatar',
            project.image306 === '' && 'no-image',
          )}
          showTooltip={showTooltip}
          hideTooltip={hideTooltip}
          updateTooltip={updateTooltip}
          tooltipElement={tooltipElement}
        />
      </div>
      <div className="project-item__name project-card__name">
        {project.weddingName}
      </div>
      <div className="project-item__date project-card__date">
        {formatDate(project.weddingDate, dateFormat)}
      </div>
      {showService && serviceType && (
        <div className="project-item__metadata project-item__service-type project-card__service-type">
          {serviceType}
        </div>
      )}
      {showLocation && weddingAddress && (
        <div className="project-item__metadata project-item__location project-card__location">
          {weddingAddress}
        </div>
      )}
      {showLead && weddingLeadName && (
        <div className="project-item__metadata project-item__lead-assignment project-card__lead-assignment">
          Lead: {weddingLeadName}
        </div>
      )}
      {isProcessing && (
        <div>Processing...</div>
      )}
      {isError && (
        <div className="project-item__actions project-card__actions">
          <div className="error-message">Error creating project</div>
          <button data-id={project.weddingId}
            className='button--unstyled'
            onClick={isPausedProject ? () => showPausedModal(dispatch) : action2}
          >
            {name2}
          </button>
        </div>
      )}
      {(project.isOwner && !isError) && (
        <div className="project-item__actions project-card__actions">
          <button data-id={project.weddingId}
            className='button--unstyled'
            onClick={isPausedProject ? () => showPausedModal(dispatch) : action1}
          >
            {name1}
          </button>
          <button data-id={project.weddingId}
            className='button--unstyled'
            onClick={isPausedProject ? () => showPausedModal(dispatch) : action2}
          >
            {name2}
          </button>
        </div>
      )}
    </div>
  );
};
